import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiAppLegacy, USER_CONSENTS_QUERY_KEY } from '@/shared/api';
import { toRawConsents } from '@/shared/data-mappers';
import type { UserConsent } from '@/shared/data-types';

/**
 * 수신·마케팅 동의 수정 mutation — `PUT /users/me/consents`. caller 가 토글 반영된 **전체 목록**을
 * 넘긴다(서버 스냅샷 보존).
 *
 * 낙관 업데이트: `onMutate` 가 consents query 캐시를 즉시 교체, `onError` 롤백, `onSettled` 무효화로
 * 서버 진실 재동기화. 호출처 feature 는 로컬 state 없이 query 만 읽는다. 후처리(토스트)는 caller 주입.
 */
export function useUpdateUserConsentsMutation() {
  const api = useApiAppLegacy();
  const queryClient = useQueryClient();

  return useMutation<void, Error, UserConsent[], { previous?: UserConsent[] }>({
    mutationFn: async (consents) => {
      await api.user.updateConsents(toRawConsents(consents));
    },
    onMutate: async (consents) => {
      await queryClient.cancelQueries({ queryKey: USER_CONSENTS_QUERY_KEY });
      const previous = queryClient.getQueryData<UserConsent[]>(USER_CONSENTS_QUERY_KEY);
      queryClient.setQueryData<UserConsent[]>(USER_CONSENTS_QUERY_KEY, consents);
      return { previous };
    },
    onError: (_error, _consents, context) => {
      if (context?.previous) {
        queryClient.setQueryData(USER_CONSENTS_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: USER_CONSENTS_QUERY_KEY });
    },
  });
}
